import * as CONSTRUCTOR from '../../generate';

/**
 * The Vietnam deposit on a Showroom booking, read through mhub-lead-integrations.
 *
 * Field names are checked against mhub-lead-integrations `schema.graphql` (PR #50), which is the
 * deployed contract: `BookingDepositResponse` wrapping `[BookingFeePayment]`. Every field
 * selected below is declared there. That matters more than it looks — this repo has no SDL of
 * its own, so nothing catches a wrong field until AppSync rejects the whole request at runtime.
 *
 * A payment's `pictures` are `BookingFeePaymentPicture { id name key size }`. There is
 * deliberately **no `url`** on them: the proxy maps the Showroom API's `paymentPictures`
 * straight through and the Showroom API names no address for a payment proof, so the panel
 * shows the file's name and does not offer a download it cannot perform. The receipt is the one
 * deposit file the CRM can actually fetch, and it has its own query below.
 *
 * Fields the schema declares that this fragment does not ask for, should a screen ever want
 * them: `opportunity_id`, `payment_description`, `total_payment_amount` (the Showroom's running
 * total, which unlike `total_paid` also counts payments awaiting approval), and a payment's
 * `payment_method_name`, `rejected_at`, `rejected_name` and `creator_user_id`.
 *
 * Two notes for whoever integrates this against the real schema:
 *
 *  - `amount_cents` is đồng × 100 even for VND (mhub-book-api `handlers/booking_fee_payments.go`),
 *    which is why `amount_text` is requested alongside it — the server builds that string with the
 *    same formatter as the printed deposit receipt, so taking it verbatim is the only way screen
 *    and receipt cannot disagree. Note the asymmetry, which is book-api's and not ours:
 *    `payment_amount`, `total_paid` and `outstanding` are **whole currency units** (they come
 *    from `bookingFeePaymentList`'s `paymentAmount` / `approvedAmount` / `outstandingAmount`
 *    float64s), while a payment's own `amount_cents` really is cents. `*_text` mirrors
 *    book-api's `paymentAmountText` / `approvedAmountText` / `outstandingAmountText` and is
 *    preferred over either, so the arithmetic never has to be repeated here.
 *  - `receipt_url` is the one field beyond the plan's stated list, and it is a **signal, not a
 *    link**. It is where the PDF lives on book-api's `/bms/` surface, which authorises on an
 *    `X-Token` header no browser holds, so an `href` to it gives the CRM user a 403. Its only
 *    job here is to say which payments have a receipt at all; the document itself comes from
 *    `FETCH_BOOKING_DEPOSIT_RECEIPT` below, which the proxy serves with the token. If
 *    `receipt_url` is absent the panel simply shows no receipt row — see DepositSection.jsx.
 */
export const fetchBookingDepositResponse = CONSTRUCTOR.FRAGMENT({
  name: 'BookingDepositResponse',
  fragmentName: 'FetchBookingDepositResponseFields',
  fields: `{
    booking_id
    booking_code
    unit_number
    payment_amount
    payment_amount_text
    total_paid
    total_paid_text
    outstanding
    outstanding_text
    payments {
      id
      booking_id
      amount_cents
      amount_text
      payment_method_type_id
      reference
      status
      cancelled_reason
      approved_at
      approved_name
      paid_by
      receipt_url
      created_at
      updated_at
      pictures {
        id
        name
        key
        size
      }
    }
  }`,
});

/**
 * The deposit receipt PDF itself, as `Query.fetchBookingDepositReceipt` returns it.
 *
 * Why a query and not a link: `receipt_url` above is a location on mhub-book-api's `/bms/`
 * surface, and `/bms/` authorises by comparing an `X-Token` header against book-api's own
 * `BMS_TOKEN`. A browser holds no such token, so putting `receipt_url` in an `href` gives the
 * CRM user a 403 and nothing else. The proxy fetches that URL server-side with the token and
 * hands the document back base64-encoded, the same shape `streamSharedMediaFile` uses for
 * shared media — see mhub-lead-integrations `integrations/srbproxies/bms/tokens/deposit.go`.
 *
 * `url` is a ready-made `data:<content_type>;base64,<file_content>`. It is preferred over
 * assembling one here; `depositReceiptDataUrl` in `utils/deposit.js` falls back to building it
 * from `content_type` + `file_content` so an older proxy that omits `url` still works.
 */
export const fetchBookingDepositReceiptResponse = CONSTRUCTOR.FRAGMENT({
  name: 'BookingDepositReceiptResponse',
  fragmentName: 'FetchBookingDepositReceiptResponseFields',
  fields: `{
    file_content
    content_type
    content_disposition
    file_name
    url
  }`,
});
